import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Typography, Space, Button, Card, Form, Input, DatePicker, Radio, Breadcrumb, Row, Col, Upload, Tag, Spin, Tooltip, Checkbox, Image } from 'antd';
import { InfoCircleOutlined, PlusOutlined, EditOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import MapSearchModal from '../components/MapSearchModal';
import ColorSchemeManager from '../components/ColorSchemeManager';
import RestaurantPreviewModal from '../components/RestaurantPreviewModal';
import { showConfirmModal } from '../components/PopupModal';
import restaurantGuidesService from '../services/restaurantGuides';
import uploadService from '../services/upload';
import {
  RestaurantGuideApiImage,
  RestaurantColorScheme,
  RestaurantGuideCoordinates,
} from '../types/restaurant';
import { message } from '../utils/message';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';

const { RangePicker } = DatePicker;

const MAX_IMAGE_COUNT = 6;

const { Title, Text } = Typography;
const { TextArea } = Input;

const RestaurantAdd: React.FC = () => {
  const navigate = useNavigate();
  const { clearAllBlockers } = useNavigationBlockerContext();
  const [form] = Form.useForm();
  const [selectedColorScheme, setSelectedColorScheme] = useState<RestaurantColorScheme | ''>('');
  const [selectedApiImageIds, setSelectedApiImageIds] = useState<string[]>([]);
  const [selectedUploadFileUids, setSelectedUploadFileUids] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const [storeNameValue, setStoreNameValue] = useState('');
  const [addressValue, setAddressValue] = useState('');
  const [detailAddressValue, setDetailAddressValue] = useState('');
  const [apiImages, setApiImages] = useState<RestaurantGuideApiImage[]>([]);
  const [apiImageLoading, setApiImageLoading] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedCoordinates, setSelectedCoordinates] = useState<RestaurantGuideCoordinates | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [colorSchemes, setColorSchemes] = useState<Array<{ key: RestaurantColorScheme; displayName: string; colors: string[] }>>([]);
  const [colorSchemeModalVisible, setColorSchemeModalVisible] = useState(false);
  const [walkingDistanceMeters, setWalkingDistanceMeters] = useState<number | null>(null);
  const [walkingDurationMinutes, setWalkingDurationMinutes] = useState<number | null>(null);
  const [layoutNameValue, setLayoutNameValue] = useState('');
  const [guideNameValue, setGuideNameValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [isModified, setIsModified] = useState(false);

  useNavigationBlocker(isModified, '/restaurants');

  // URL 정제 유틸리티 함수
  const cleanImageUrl = (url: string): string => {
    if (!url) return url;

    let cleanedUrl = url.trim();

    // 1단계: 외부 따옴표 제거 (반복 처리)
    while (
      (cleanedUrl.startsWith('"') && cleanedUrl.endsWith('"')) ||
      (cleanedUrl.startsWith("'") && cleanedUrl.endsWith("'"))
    ) {
      cleanedUrl = cleanedUrl.slice(1, -1).trim();
    }

    // 2단계: src 파라미터에서 실제 URL 추출
    // src="URL" 또는 src=URL 형태 모두 처리
    const srcMatch = cleanedUrl.match(/[?&]src=([^&]+)/);
    if (srcMatch) {
      let extractedUrl = srcMatch[1];

      // src 파라미터 값의 앞뒤 따옴표 제거
      while (
        (extractedUrl.startsWith('"') && extractedUrl.endsWith('"')) ||
        (extractedUrl.startsWith("'") && extractedUrl.endsWith("'")) ||
        (extractedUrl.startsWith('%22') && extractedUrl.endsWith('%22'))
      ) {
        extractedUrl = extractedUrl.slice(
          extractedUrl.startsWith('%22') ? 3 : 1,
          extractedUrl.endsWith('%22') ? -3 : -1
        ).trim();
      }

      // URL 디코딩
      try {
        cleanedUrl = decodeURIComponent(extractedUrl);
      } catch (e) {
        cleanedUrl = extractedUrl;
      }
    }

    // 3단계: 최종 정제 - 남은 따옴표 제거
    cleanedUrl = cleanedUrl.replace(/^["']+|["']+$/g, '');

    return cleanedUrl;
  };


  const totalSelectedImages = useMemo(
    () => selectedApiImageIds.length + selectedUploadFileUids.length,
    [selectedApiImageIds, selectedUploadFileUids]
  );

  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          setIsModified(false);
          setTimeout(() => {
            navigate('/restaurants');
          }, 0);
        },
      });
    } else {
      navigate('/restaurants');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      form.setFieldValue('hashtags', selectedTags);
      form.setFieldValue('colorScheme', selectedColorScheme);

      const values = await form.validateFields();

      if (!selectedColorScheme) {
        message.error('레이아웃 색상을 선택해주세요.');
        return;
      }

      if (selectedApiImageIds.length + selectedUploadFileUids.length === 0) {
        message.error('최소 1개의 이미지를 선택해주세요.');
        return;
      }

      const uploadTargets = fileList
        .filter((file) => selectedUploadFileUids.includes(file.uid) && !!file.originFileObj)
        .map((file) => file.originFileObj as File);

      const directUploadedImages = await restaurantGuidesService.uploadImages(uploadTargets);

      // API 이미지 중 선택되고 변환된 이미지만 uploadedImages에 추가
      const convertedApiImages = apiImages
        .filter(img => {
          const isSelected = selectedApiImageIds.includes(img.id);
          const isConverted = (img as any).isConverted && img.url && (img as any).convertedFilename;
          return isSelected && isConverted;
        })
        .map(img => ({
          filename: (img as any).convertedFilename!,
          originalName: img.title || 'API Image',
          url: img.url,
          size: 0,
          originalUrl: (img as any).originalUrl || img.url
        }));

      const uploadedImages = [...directUploadedImages, ...convertedApiImages];

      // selectedApiImageIds 중에서 변환된 이미지는 upload: prefix로 변경
      const convertedApiImageMap = new Map(
        convertedApiImages.map(img => [img.originalUrl, `upload:${img.filename}`])
      );

      const transformedApiImageIds = selectedApiImageIds.map(apiId => {
        // 'api:' prefix 제거하고 원본 URL 추출
        const originalUrl = apiId.replace('api:', '');
        // 변환된 이미지 맵에서 찾기
        return convertedApiImageMap.get(originalUrl) || apiId;
      });

      const selectedImageIds = [
        ...transformedApiImageIds,
        ...directUploadedImages.map((image) => `upload:${image.filename}`),
      ];

      const payload = {
        layoutName: values.layoutName,
        colorScheme: selectedColorScheme,
        exposure: values.exposure,
        startDate: values.alwaysVisible || values.exposure === 'hide'
          ? null
          : (values.exposurePeriod && values.exposurePeriod[0] ? values.exposurePeriod[0].format('YYYY-MM-DD') : null),
        endDate: values.alwaysVisible || values.exposure === 'hide'
          ? null
          : (values.exposurePeriod && values.exposurePeriod[1] ? values.exposurePeriod[1].format('YYYY-MM-DD') : null),
        storeName: values.storeName,
        address: values.address,
        detailAddress: values.detailAddress || '',
        coordinates: selectedCoordinates,
        walkingDistanceMeters,
        walkingDurationMinutes,
        guideName: values.guideName,
        hashtags: selectedTags,
        description: values.description,
        apiImages,
        uploadedImages,
        selectedImageIds,
        status: 'draft' as const,
        displayOrder: 0,
      };

      // console.log('📦 제출할 payload:', payload);
      // console.log('🚶 도보 거리 상태:', { walkingDistanceMeters, walkingDurationMinutes });

      const createdGuide = await restaurantGuidesService.createGuide(payload);

      message.success('성공적으로 추가되었습니다.');
      clearAllBlockers();
      navigate(`/restaurants/detail/${createdGuide.id}`);
    } catch (error: any) {
      console.error('맛집 가이드 등록 실패:', error);
      if (error?.errorFields) {
        message.error('필수 입력 항목을 확인해주세요.');
      } else {
        const errorMessage = error?.response?.data?.message || '맛집 가이드 등록 중 오류가 발생했습니다.';
        message.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 상호명으로 이미지 검색 API 호출
  const fetchRestaurantImages = async (storeName: string) => {
    if (!storeName.trim()) {
      setApiImages([]);
      return;
    }

    try {
      setApiImageLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/search/restaurant-images?query=${encodeURIComponent(storeName)}`);
      const data = await response.json();

      if (data.success && Array.isArray(data.items)) {
        const limitedImages: RestaurantGuideApiImage[] = data.items.slice(0, 12).map((item: any, index: number) => {
          let highResUrl = item.originalImage || item.link;

          // URL 정제: 이중 인코딩된 src 파라미터 처리
          if (highResUrl && typeof highResUrl === 'string') {
            // 1단계: 불필요한 따옴표 제거
            highResUrl = highResUrl.trim();
            if (highResUrl.startsWith('"') && highResUrl.endsWith('"')) {
              highResUrl = highResUrl.slice(1, -1);
            }
            if (highResUrl.startsWith("'") && highResUrl.endsWith("'")) {
              highResUrl = highResUrl.slice(1, -1);
            }

            // 2단계: src 파라미터 내의 실제 URL 추출
            const srcMatch = highResUrl.match(/[?&]src=["']?([^"'&]+)["']?/);
            if (srcMatch) {
              highResUrl = decodeURIComponent(srcMatch[1]);
            }
          }

          // 모든 네이버 관련 URL에서 크기 조정 파라미터 제거
          if (highResUrl && (highResUrl.includes('blogfiles.naver.net') || highResUrl.includes('pstatic.net'))) {
            highResUrl = highResUrl.replace(/\?type=w\d+/, '').replace(/&type=w\d+/, '');
            highResUrl = highResUrl.replace(/\?type=w/, '').replace(/&type=w/, '');
            highResUrl = highResUrl.replace(/\?w=\d+/, '').replace(/&w=\d+/, '');
            highResUrl = highResUrl.replace(/\?h=\d+/, '').replace(/&h=\d+/, '');
            // 마지막에 남은 '?' 또는 '&' 정리
            highResUrl = highResUrl.replace(/[?&]$/, '');
          }

          const baseId = item.link || item.thumbnail || `index-${index}`;

          return {
            id: `api:${baseId}`,
            url: highResUrl || item.link,
            thumbnail: item.thumbnailImage || item.thumbnail || highResUrl,
            originalUrl: item.link,
            title: item.title,
            sizewidth: item.sizewidth,
            sizeheight: item.sizeheight,
          };
        });

        const availableIds = new Set(limitedImages.map((image) => image.id));
        setApiImages(limitedImages);
        setSelectedApiImageIds((prev) => prev.filter((id) => availableIds.has(id)));
      } else {
        setApiImages([]);
        setSelectedApiImageIds([]);
      }
    } catch (error) {
      console.error('이미지 검색 실패:', error);
      setApiImages([]);
      setSelectedApiImageIds([]);
      message.error('이미지 검색 중 오류가 발생했습니다.');
    } finally {
      setApiImageLoading(false);
    }
  };

  // 활성화된 컬러스킴 로드 함수
  const fetchActiveColorSchemes = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cms/color-schemes/active`);
      const data = await response.json();
      if (data.success) {
        setColorSchemes(data.data as Array<{ key: RestaurantColorScheme; displayName: string; colors: string[] }>);
      }
    } catch (error) {
      console.error('컬러스킴 조회 실패:', error);
    }
  }, []);

  // 컬러스킴 로드
  useEffect(() => {
    const loadColorSchemes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cms/color-schemes/active`);
        const data = await response.json();
        if (data.success) {
          const schemes = data.data as Array<{ key: RestaurantColorScheme; displayName: string; colors: string[] }>;
          setColorSchemes(schemes);
        }
      } catch (error) {
        console.error('컬러스킴 조회 실패:', error);
      }
    };

    loadColorSchemes();
  }, []);

  // 컬러스킴 로드 후 첫 번째 값 자동 선택
  useEffect(() => {
    if (colorSchemes.length > 0) {
      const firstScheme = colorSchemes[0].key;
      setSelectedColorScheme(firstScheme);
      form.setFieldsValue({ colorScheme: firstScheme });
    }
  }, [colorSchemes, form]);

  // 상호명 변경 시 이미지 검색
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (storeNameValue) {
        fetchRestaurantImages(storeNameValue);
      } else {
        setApiImages([]);
        setSelectedApiImageIds([]);
        setSelectedCoordinates(null);
      }
    }, 500); // 0.5초 debounce

    return () => clearTimeout(timeoutId);
  }, [storeNameValue]);

  // API 이미지 선택
  const handleApiImageSelect = async (image: RestaurantGuideApiImage) => {
    const isSelected = selectedApiImageIds.includes(image.id);

    if (isSelected) {
      // 선택 해제
      setSelectedApiImageIds((prev) => prev.filter((id) => id !== image.id));
      return;
    }

    // 선택 가능 여부 확인
    const currentTotalSelected = selectedApiImageIds.length + selectedUploadFileUids.length;
    if (currentTotalSelected >= MAX_IMAGE_COUNT) {
      message.warning(`API 제공 이미지와 첨부파일을 합쳐 최대 ${MAX_IMAGE_COUNT}개까지만 선택할 수 있습니다.`);
      return;
    }

    // API 이미지를 GCS에 업로드 (PNG 변환 포함)
    try {
      message.loading({ content: '이미지 변환 중...', key: 'convert-image' });

      // URL 정제 후 업로드
      const cleanedUrl = cleanImageUrl(image.url);
      let response: any;

      try {
        response = await uploadService.uploadImageFromUrl(cleanedUrl, image.title);
      } catch (firstError: any) {
        // 원본 URL 실패 시 썸네일 URL로 재시도 (400, 404, 429 등)
        const retryableStatuses = [400, 404, 429, 500];
        if (
          retryableStatuses.includes(firstError?.response?.status) &&
          image.thumbnail &&
          image.thumbnail !== image.url
        ) {
          console.log('원본 URL 실패, 썸네일 URL로 재시도:', image.thumbnail);
          const cleanedThumbnail = cleanImageUrl(image.thumbnail);
          response = await uploadService.uploadImageFromUrl(cleanedThumbnail, image.title);
        } else {
          throw firstError;
        }
      }

      if (response.success) {
        // 변환된 이미지 정보로 apiImages 업데이트
        setApiImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                  ...img,
                  url: response.data.url,
                  thumbnail: response.data.url,
                  isConverted: true,
                  convertedFilename: response.data.filename
                }
              : img
          )
        );

        // 선택 목록에 추가
        setSelectedApiImageIds((prev) => [...prev, image.id]);
        message.success({ content: '이미지가 변환되었습니다', key: 'convert-image' });
      }
    } catch (error: any) {
      console.error('API 이미지 변환 실패:', error);
      message.error({
        content: error?.response?.data?.message || '이미지 변환에 실패했습니다',
        key: 'convert-image'
      });
    }
  };

  // API 이미지 전체 선택/해제
  const handleSelectAllApiImages = (e: any) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      // 선택할 수 있는 만큼만 전체 선택
      const currentSelectedCount = selectedUploadFileUids.length;
      const availableSlots = MAX_IMAGE_COUNT - currentSelectedCount;
      const imagesToSelect = apiImages.slice(0, availableSlots).map((img) => img.id);
      setSelectedApiImageIds(imagesToSelect);

      if (availableSlots < apiImages.length) {
        message.info(`${apiImages.length - availableSlots}개 이미지는 선택 제한(${MAX_IMAGE_COUNT}개)으로 인해 선택되지 않았습니다.`);
      }
    } else {
      // 전체 해제
      setSelectedApiImageIds([]);
    }
  };

  // 파일 업로드 처리
  const handleFileUpload = ({ fileList: incomingList }: UploadChangeParam<UploadFile>) => {
    const validatedList = incomingList.filter((uploadFile) => {
      const origin = uploadFile.originFileObj as File | undefined;
      if (!origin) {
        return true;
      }

      if (!origin.type.startsWith('image/')) {
        message.error(`${origin.name}은(는) 이미지 파일이 아닙니다.`);
        return false;
      }

      if (origin.size / 1024 / 1024 >= 5) {
        message.error(`${origin.name}의 크기가 5MB를 초과합니다.`);
        return false;
      }

      return true;
    }).map((uploadFile) => {
      if (uploadFile.originFileObj && !uploadFile.url && !uploadFile.preview) {
        uploadFile.preview = URL.createObjectURL(uploadFile.originFileObj as File);
      }
      return uploadFile;
    });

    // 새로 추가된 파일만 fileList에 추가
    const newFiles = validatedList.filter(
      (newFile) => !fileList.some((existingFile) => existingFile.uid === newFile.uid)
    );

    const updatedFileList = [...fileList, ...newFiles];
    setFileList(updatedFileList);

    // 새로 업로드된 파일 자동 선택 (최대 개수 체크)
    let currentSelectedCount = selectedApiImageIds.length + selectedUploadFileUids.length;
    const newSelectedUids: string[] = [];

    newFiles.forEach((file) => {
      if (currentSelectedCount < MAX_IMAGE_COUNT) {
        newSelectedUids.push(file.uid);
        currentSelectedCount++;
      }
    });

    if (newSelectedUids.length > 0) {
      setSelectedUploadFileUids((prev) => [...prev, ...newSelectedUids]);
    }

    // 6개 제한을 초과하여 자동 선택되지 않은 파일이 있으면 안내
    if (newFiles.length > newSelectedUids.length) {
      message.info(`${newFiles.length - newSelectedUids.length}개 파일이 업로드되었지만 선택 제한(6개)으로 인해 비선택 상태입니다.`);
    }
  };

  // 업로드된 파일 선택/해제
  const handleUploadFileSelect = (uid: string) => {
    setSelectedUploadFileUids((prev) => {
      const isSelected = prev.includes(uid);
      if (isSelected) {
        return prev.filter((id) => id !== uid);
      }

      // setState 내부에서 현재 상태(prev)로 직접 계산
      const currentTotalSelected = selectedApiImageIds.length + prev.length;
      if (currentTotalSelected >= MAX_IMAGE_COUNT) {
        message.warning(`API 제공 이미지와 첨부파일을 합쳐 최대 ${MAX_IMAGE_COUNT}개까지만 선택할 수 있습니다.`);
        return prev;
      }

      return [...prev, uid];
    });
  };


  const syncTagsToForm = (tags: string[]) => {
    form.setFieldValue('hashtags', tags);
    form.validateFields(['hashtags']).catch(() => undefined);
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed.length > 20) {
      message.error('해시태그는 최대 20자까지 입력 가능합니다.');
      return;
    }
    if (trimmed && !selectedTags.includes(trimmed) && selectedTags.length < 4) {
      const nextTags = [...selectedTags, trimmed];
      setSelectedTags(nextTags);
      syncTagsToForm(nextTags);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const nextTags = selectedTags.filter(tag => tag !== tagToRemove);
    setSelectedTags(nextTags);
    syncTagsToForm(nextTags);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleMapSearch = () => {
    setMapModalVisible(true);
  };

  const handleMapModalClose = () => {
    setMapModalVisible(false);
  };

  const handleLocationSelect = (location: {
    id: string;
    name: string;
    address: string;
    roadAddress: string;
    x: number;
    y: number;
    category?: string;
    telephone?: string;
    walkingDistanceMeters?: number;
    walkingDurationMinutes?: number;
  }) => {
    // console.log('📍 RestaurantAdd - 위치 선택됨:', location);
    const nextAddress = location.roadAddress || location.address;

    setStoreNameValue(location.name);
    setAddressValue(nextAddress);
    setSelectedCoordinates({ lat: location.y, lon: location.x });
    setWalkingDistanceMeters(location.walkingDistanceMeters || null);
    setWalkingDurationMinutes(location.walkingDurationMinutes || null);

    // console.log('🚶 RestaurantAdd - 도보 거리 상태 설정:', {
    //   walkingDistanceMeters: location.walkingDistanceMeters,
    //   walkingDurationMinutes: location.walkingDurationMinutes
    // });

    form.setFieldValue('storeName', location.name);
    form.setFieldValue('address', nextAddress);

    // 선택된 장소명으로 이미지 검색 실행
    fetchRestaurantImages(location.name);

    setMapModalVisible(false);
  };

  const handlePreview = () => {
    // 필수 값 검증
    if (!storeNameValue) {
      message.warning('상호명을 입력해주세요.');
      return;
    }
    if (!addressValue) {
      message.warning('주소를 입력해주세요.');
      return;
    }
    if (totalSelectedImages === 0) {
      message.warning('최소 1개의 이미지를 선택해주세요.');
      return;
    }

    setPreviewModalVisible(true);
  };

  // 미리보기용 레스토랑 데이터 생성
  const getPreviewRestaurantData = () => {
    const values = form.getFieldsValue();
    return {
      id: 0,
      layoutName: layoutNameValue || '레이아웃명',
      colorScheme: selectedColorScheme || 'red-yellow',
      exposure: values.exposure || 'show',
      startDate: values.exposurePeriod?.[0]?.format('YYYY-MM-DD') || new Date().toISOString(),
      endDate: values.exposurePeriod?.[1]?.format('YYYY-MM-DD') || new Date().toISOString(),
      storeName: storeNameValue || '상호명',
      address: addressValue || '주소',
      detailAddress: detailAddressValue || '',
      coordinates: selectedCoordinates,
      walkingDistanceMeters,
      walkingDurationMinutes,
      guideName: guideNameValue || '가이드명',
      hashtags: selectedTags,
      description: descriptionValue || '설명',
      apiImages: apiImages,
      uploadedImages: [],
      selectedImageIds: [...selectedApiImageIds, ...selectedUploadFileUids],
      status: 'draft' as const,
      displayOrder: 0,
      viewCount: 0,
      createdAt: dayjs().toISOString(),
      updatedAt: dayjs().toISOString(),
    };
  };

  // 미리보기용 DisplayImage 배열 생성
  const getPreviewDisplayImages = () => {
    const displayImages: Array<{
      id: string;
      url: string;
      type: 'api' | 'upload';
      title?: string;
    }> = [];

    // 선택된 API 이미지 추가
    selectedApiImageIds.forEach((imageId) => {
      const apiImage = apiImages.find((img) => img.id === imageId);
      if (apiImage) {
        displayImages.push({
          id: apiImage.id,
          url: apiImage.url,
          type: 'api',
          title: apiImage.title,
        });
      }
    });

    // 선택된 업로드 파일 추가
    selectedUploadFileUids.forEach((uid) => {
      const uploadFile = fileList.find((file) => file.uid === uid);
      if (uploadFile && (uploadFile.preview || uploadFile.url)) {
        displayImages.push({
          id: uid,
          url: uploadFile.preview || uploadFile.url || '',
          type: 'upload',
          title: uploadFile.name,
        });
      }
    });

    return displayImages;
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '라이프&컬쳐' },
          { title: '맛집 가이드', href: '/restaurants' },
          { title: '맛집 가이드 추가' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          맛집 가이드 추가
        </Title>
        <Button onClick={handlePreview}>미리보기</Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        style={{ width: '100%' }}
        initialValues={{
          layoutName: '',
          colorScheme: '',
          exposure: 'show',
          alwaysVisible: false,
          exposurePeriod: [dayjs(), dayjs().add(1, 'day')],
          storeName: '',
          address: '',
          guideName: '',
          description: '',
          hashtags: [],
        }}
      
        onValuesChange={() => setIsModified(true)}
      >
        {/* 기본 정보 */}
        <Card title="기본 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>레이아웃명 <span style={{ color: '#ff4d4f' }}>*</span></span>
                    <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                    <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                      영문 대문자 기준 6자 이후 강제 줄바꿈 처리됩니다.
                    </Text>
                  </div>
                }
                name="layoutName"
                rules={[
                  { required: true, message: '레이아웃명을 입력하세요.' },
                  { max: 35, message: '레이아웃명은 35자 이내로 입력하세요.' }
                ]}
              >
                <TextArea
                  rows={5}
                  placeholder="레이아웃명을 입력하세요."
                  maxLength={35}
                  value={layoutNameValue}
                  onChange={(e) => {
                    let value = e.target.value;
                    // 6자마다 자동 줄바꿈 처리 (기존 줄바꿈 유지)
                    const lines = value.split('\n');
                    const processedLines = lines.map(line => {
                      // 이미 줄바꿈이 있는 경우 유지
                      if (line.length <= 6) return line;
                      // 6자마다 줄바꿈 삽입
                      return line.match(/.{1,6}/g)?.join('\n') || line;
                    });
                    value = processedLines.join('\n');

                    setLayoutNameValue(value);
                    form.setFieldValue('layoutName', value);
                  }}
                  count={{
                    show: true,
                    max: 35,
                  }}
                  style={{ width: '58ch' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col span={24}>
              <Form.Item
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>레이아웃 색상 <span style={{ color: '#ff4d4f' }}>*</span></span>
                  </div>
                }
                name="colorScheme"
                rules={[{ required: true, message: '레이아웃 색상을 선택하세요.' }]}
              >
                <Radio.Group value={selectedColorScheme} onChange={(e) => setSelectedColorScheme(e.target.value)}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    {colorSchemes.map((scheme) => (
                      <div
                        key={scheme.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0px',
                          cursor: 'pointer',
                          padding: '8px',
                          borderRadius: '6px',
                          border: selectedColorScheme === scheme.key ? '0px solid #F24822' : '0px solid #303030'
                        }}
                        onClick={() => setSelectedColorScheme(scheme.key)}
                      >
                        <Radio value={scheme.key} />
                        <Tooltip
                          title={
                            <div style={{ fontSize: '12px', textAlign: 'left' }}>
                              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>{scheme.displayName}</div>
                              <div>Primary : {scheme.colors[0]?.toUpperCase() || '-'}</div>
                              <div>Secondary : {scheme.colors[1]?.toUpperCase() || '-'}</div>
                            </div>
                          }
                          placement="top"
                          color="#000000"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            {/* Primary 컬러 (앞쪽) */}
                            <div
                              style={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: scheme.colors[0],
                                borderRadius: '50%',
                                position: 'relative',
                                zIndex: 2,
                              }}
                            />
                            {/* Second 컬러 (뒤쪽, 겹침) */}
                            {scheme.colors[1] && (
                              <div
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  backgroundColor: scheme.colors[1],
                                  borderRadius: '50%',
                                  position: 'relative',
                                  marginLeft: '-22px',
                                  zIndex: 1,
                                }}
                              />
                            )}
                          </div>
                        </Tooltip>
                      </div>
                    ))}
                    {/* <Text style={{ color: '#a6a6a6' }}>Primary</Text> */}
                  </div>
                </Radio.Group>
              </Form.Item>
              {/* Form.Item 외부로 설명 텍스트와 버튼 분리 */}
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>레이아웃의 포인트 컬러를 설정합니다.<br />적용 영역 : 레이아웃명, 해시태그, 거리</Text>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setColorSchemeModalVisible(true)}
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    padding: '0 4px',
                    height: 20
                  }}
                >
                  색상편집
                </Button>
              </div>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Form.Item
                label={<span>노출여부 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                name="exposure"
                rules={[{ required: true, message: '노출 여부를 선택하세요.' }]}
              >
                <Radio.Group>
                  <Radio value="show">노출</Radio>
                  <Radio value="hide">비노출</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col span={24}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.exposure !== currentValues.exposure ||
                  prevValues.alwaysVisible !== currentValues.alwaysVisible
                }
              >
                {({ getFieldValue }) => {
                  const exposure = getFieldValue('exposure');
                  const alwaysVisible = getFieldValue('alwaysVisible');
                  const isExposureShow = exposure === 'show';
                  const isDatePickerDisabled = !isExposureShow || alwaysVisible;

                  return (
                    <>
                      <Row gutter={[24, 16]}>
                        <Col span={6}>
                          <Form.Item
                            label={<span>노출 기간</span>}
                            name="exposurePeriod"
                            rules={[
                              {
                                required: isExposureShow && !alwaysVisible,
                                message: '노출 기간을 선택해주세요.',
                              },
                            ]}
                          >
                            <RangePicker
                              format="YYYY.MM.DD"
                              disabled={isDatePickerDisabled}
                              style={{ width: '100%', background: '#141414', border: '1px solid #303030' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item
                            name="alwaysVisible"
                            valuePropName="checked"
                            style={{ marginTop: '30px' }}
                          >
                            <Checkbox
                              disabled={!isExposureShow}
                              checked={alwaysVisible}
                              onChange={(e) => {
                                form.setFieldValue('alwaysVisible', e.target.checked);
                                if (e.target.checked) {
                                  form.setFieldValue('exposurePeriod', null);
                                }
                              }}
                              style={{ color: isExposureShow ? '#ffffff' : '#666666' }}
                            >
                              상시 노출
                            </Checkbox>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '36px'
                          }}>
                            <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                              기간이 종료되면 자동 비공개 처리됩니다.
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    </>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 음식점 정보 */}
        <Card title="음식점 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item
                label={<span>상호명 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                name="storeName"
                rules={[{ required: true, message: '상호명을 입력하세요.' }]}
              >
                <Space.Compact style={{ display: 'flex', width: '100%' }}>
                  <Input
                    style={{ flex: 1 }}
                    value={storeNameValue}
                    placeholder='상호명을 입력하세요.'
                    disabled
                  />
                  <Button type="primary" onClick={handleMapSearch}>지도 검색</Button>
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item
                label={<span>주소 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                name="address"
                rules={[{ required: true, message: '주소를 입력하세요.' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  value={addressValue}
                  disabled
                  placeholder="주소를 검색하세요."
                  onChange={(e) => {
                    setAddressValue(e.target.value);
                    form.setFieldValue('address', e.target.value);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={6} style={{ marginTop: '8px' }}>
              <Form.Item
                name="detailAddress"
              >
                <Input
                  value={detailAddressValue}
                  placeholder="상세 주소를 입력하세요."
                  onChange={(e) => {
                    setDetailAddressValue(e.target.value);
                    form.setFieldValue('detailAddress', e.target.value);
                  }}
                />
              </Form.Item>
            </Col>
            {walkingDistanceMeters && walkingDurationMinutes && (
              <Col span={24} style={{ marginTop: '-16px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  🚶 도보 {walkingDurationMinutes}분 ({walkingDistanceMeters}m)
                </Text>
              </Col>
            )}
          </Row>
        </Card>

        {/* 이미지 정보 */}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '16px', alignItems: 'center', width: '100%' }}>
              <span>이미지 정보 ({totalSelectedImages}/{MAX_IMAGE_COUNT})</span>
              <Button onClick={() => storeNameValue && fetchRestaurantImages(storeNameValue)}>
                API 새로고침
              </Button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  API 제공 이미지가 표시되지 않는 경우 새로고침 해주세요.
                </Text>
              </div>
            </div>
          }
          style={{ marginBottom: '24px' }}
        >
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>API 제공</Text>
                <Checkbox
                  checked={selectedApiImageIds.length === apiImages.length && selectedApiImageIds.length > 0}
                  onChange={handleSelectAllApiImages}
                  disabled={apiImages.length === 0}
                >
                  전체 선택
                </Checkbox>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              {/* <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>상호명 기반 자동 검색</Text> */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {apiImageLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Spin size="small" />
                    <Text style={{ color: '#a6a6a6' }}>이미지 검색 중...</Text>
                  </div>
                )}
              </div>
            </div>


            {!storeNameValue ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                backgroundColor: '#1a1a1a',
                borderRadius: '6px',
                marginBottom: '24px'
              }}>
                <Text style={{ color: '#a6a6a6' }}>상호명을 입력하면 관련 이미지가 표시됩니다.</Text>
              </div>
            ) : apiImages.length === 0 && !apiImageLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                backgroundColor: '#1a1a1a',
                borderRadius: '6px',
                marginBottom: '24px'
              }}>
                <Text style={{ color: '#a6a6a6' }}>검색된 이미지가 없습니다.</Text>
              </div>
            ) : (
              <Image.PreviewGroup>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 200px)', gap: '8px', marginBottom: '24px' }}>
                  {apiImages
                    .filter(image => !failedImages.has(image.url)) // 실패한 이미지 필터링
                    .map((image) => (
                    <div
                      key={image.id}
                      style={{
                        position: 'relative',
                        cursor: 'pointer',
                        border: selectedApiImageIds.includes(image.id) ? '2px solid #F24822' : '1px solid #303030',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        width: '200px',
                        height: '250px',
                        backgroundColor: '#2a2a2a'
                      }}
                      onClick={() => handleApiImageSelect(image)}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        // 숨겨진 Image 컴포넌트의 img 태그를 찾아서 클릭
                        const hiddenImg = e.currentTarget.querySelector('.preview-trigger-image img');
                        if (hiddenImg) {
                          (hiddenImg as HTMLElement).click();
                        }
                      }}
                    >
                      {/* 미리보기용 숨겨진 Image 컴포넌트 */}
                      <div className="preview-trigger-image" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        opacity: 0,
                        pointerEvents: 'none'
                      }}>
                        <Image
                          src={image.url}
                          alt={image.title}
                          preview={{
                            mask: null
                          }}
                        />
                      </div>
                      {/* 실제 표시되는 섬네일 */}
                      <img
                        src={image.url}
                        alt={image.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          imageRendering: 'auto',
                          filter: 'brightness(1.05) contrast(1.02)',
                          opacity: '0.8',
                          transition: 'opacity 0.3s ease'
                        }}
                        onError={() => {
                          // failedImages에 추가하여 전체 카드 숨김
                          setFailedImages(prev => new Set(Array.from(prev).concat(image.url)));
                        }}
                        onLoad={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      />
                      {selectedApiImageIds.includes(image.id) && (
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          left: '4px',
                          backgroundColor: '#F24822',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2
                        }}>
                          <Text style={{ color: 'white', fontSize: '12px' }}>✓</Text>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Image.PreviewGroup>
            )}

            <Text strong>첨부파일</Text>
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Image.PreviewGroup>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 200px)', gap: '8px' }}>
                  {/* 업로드된 파일 목록 */}
                  {fileList.map((file) => (
                    <div
                      key={file.uid}
                      style={{
                        position: 'relative',
                        cursor: 'pointer',
                        border: selectedUploadFileUids.includes(file.uid) ? '2px solid #F24822' : '1px solid #303030',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        width: '200px',
                        height: '250px',
                        backgroundColor: '#2a2a2a'
                      }}
                      onClick={() => handleUploadFileSelect(file.uid)}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        // 숨겨진 Image 컴포넌트의 img 태그를 찾아서 클릭
                        const hiddenImg = e.currentTarget.querySelector('.preview-trigger-image img');
                        if (hiddenImg) {
                          (hiddenImg as HTMLElement).click();
                        }
                      }}
                    >
                      {/* 미리보기용 숨겨진 Image 컴포넌트 */}
                      <div className="preview-trigger-image" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        opacity: 0,
                        pointerEvents: 'none'
                      }}>
                        <Image
                          src={file.preview || file.url}
                          alt={file.name}
                          preview={{
                            mask: null
                          }}
                        />
                      </div>
                      {/* 실제 표시되는 섬네일 */}
                      <img
                        src={file.preview || file.url}
                        alt={file.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          opacity: '0.8',
                          transition: 'opacity 0.3s ease'
                        }}
                        onLoad={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      />
                      {/* 선택 체크 마크 */}
                      {selectedUploadFileUids.includes(file.uid) && (
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          left: '4px',
                          backgroundColor: '#F24822',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2
                        }}>
                          <Text style={{ color: 'white', fontSize: '12px' }}>✓</Text>
                        </div>
                      )}
                      {/* 삭제 버튼 */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          zIndex: 3
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newFileList = fileList.filter((f) => f.uid !== file.uid);
                          setFileList(newFileList);
                          setSelectedUploadFileUids((prev) => prev.filter((uid) => uid !== file.uid));
                        }}
                      >
                        <Text style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>×</Text>
                      </div>
                    </div>
                  ))}

                {/* Upload 버튼 */}
                <Upload
                  accept="image/*"
                  multiple
                  fileList={[]}
                  onChange={handleFileUpload}
                  beforeUpload={() => false}
                  showUploadList={false}
                >
                  <div
                    style={{
                      width: '200px',
                      height: '250px',
                      border: '2px dashed #303030',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: '#141414',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#F24822';
                      e.currentTarget.style.backgroundColor = '#1f1f1f';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#303030';
                      e.currentTarget.style.backgroundColor = '#141414';
                    }}
                  >
                    <InboxOutlined style={{ fontSize: '48px', color: '#a6a6a6' }} />
                    <div style={{ marginTop: '16px', color: '#ffffff', fontSize: '14px', textAlign: 'center', lineHeight: '1.4' }}>
                      Upload
                    </div>
                  </div>
                </Upload>
              </div>
              </Image.PreviewGroup>
            </div>

            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px', marginTop: '2px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  - API 제공과 첨부파일 이미지를 포함하여 최대 6개까지 선택 가능합니다.<br />
                  - 더블 클릭 시 원본 사이즈 미리보기가 제공됩니다.<br />
                  첨부파일 형식 : png, jpeg, jpg / 5MB 이하 (최대 10개)<br />
                  권장 이미지 사이즈 : 0000px(가로) * 0000px(세로)
                </Text>
              </div>
            </div>
          </div>
        </Card>

        {/* 가이드 정보 */}
        <Card title="가이드 정보" style={{ marginBottom: '24px' }}>
          <Form.Item
            label={<span>가이드명 <span style={{ color: '#ff4d4f' }}>*</span></span>}
            name="guideName"
            rules={[
              { required: true, message: '가이드명을 입력하세요.' },
              { max: 100, message: '가이드명은 100자 이내로 입력하세요.' }
            ]}
          >
            <Input
              placeholder="가이드명을 입력하세요 (최대 100자)"
              value={guideNameValue}
              maxLength={100}
              onChange={(e) => {
                const value = e.target.value;
                setGuideNameValue(value);
                form.setFieldValue('guideName', value);
              }}
              count={{
                show: true,
                max: 100,
              }}
            />
          </Form.Item>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-12px', marginBottom: '16px' }}>
            <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
              제목은 디스플레이에 노출되지 않습니다.
            </Text>
          </div>

          <Form.Item
            label={<span>해시태그 <span style={{ color: '#ff4d4f' }}>*</span></span>}
            name="hashtags"
            rules={[
              { required: true, message: '해시태그를 최소 1개 등록해주세요.' },
              {
                validator: (_, value) => (
                  Array.isArray(value) && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(''))
                ),
              }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' }}>
                <Input
                  placeholder="해시태그를 입력하세요 (최대 20자)"
                  value={tagInput}
                  maxLength={20}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  style={{ width: '54ch' }}
                  suffix={
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={handleAddTag}
                      disabled={!tagInput.trim() || selectedTags.length >= 4}
                    >
                      추가
                    </Button>
                  }
                />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '8px', whiteSpace: 'nowrap' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px', marginTop: '2px', flexShrink: 0 }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    최대 20자, 최대 4개까지 추가 가능합니다.
                  </Text>
                </div>
              </div>

              {/* 선택된 태그들 표시 */}
              {selectedTags.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {selectedTags.map((tag) => (
                    <Tag
                      key={tag}
                      closable
                      onClose={() => handleRemoveTag(tag)}
                      style={{
                        backgroundColor: '#F24822',
                        color: 'white',
                        border: '1px solid #F24822'
                      }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
              )}

              {/* 기본 태그들 */}
              {/* <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {defaultTags.map((tag) => (
                  <Tag
                    key={tag}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                      color: '#a6a6a6',
                      border: '1px solid #303030'
                    }}
                    onClick={() => {
                      if (!selectedTags.includes(tag) && selectedTags.length < 4) {
                        const nextTags = [...selectedTags, tag];
                        setSelectedTags(nextTags);
                        syncTagsToForm(nextTags);
                      }
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
              </div> */}
            </div>
          </Form.Item>

          <Form.Item
            label={<span>내용 <span style={{ color: '#ff4d4f' }}>*</span></span>}
            name="description"
            rules={[
              { required: true, message: '내용을 입력하세요.' },
              { max: 68, message: '내용은 68자 이내로 입력하세요.' }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="내용을 입력하세요 (최대 68자)"
              maxLength={68}
              value={descriptionValue}
              onChange={(e) => {
                const value = e.target.value;
                setDescriptionValue(value);
                form.setFieldValue('description', value);
              }}
              count={{
                show: true,
                max: 68,
              }}
              style={{ width: '54ch' }}
            />
          </Form.Item>
        </Card>


        {/* 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          <Button size="large" onClick={handleCancel}>
            취소
          </Button>
          <Button type="primary" size="large" onClick={handleSubmit} loading={submitting} disabled={submitting}>
            추가
          </Button>
        </div>
      </Form>

      {/* 지도 검색 모달 */}
      <MapSearchModal
        visible={mapModalVisible}
        onClose={handleMapModalClose}
        onSelect={handleLocationSelect}
      />

      {/* 컬러스킴 관리 모달 */}
      <ColorSchemeManager
        visible={colorSchemeModalVisible}
        onCancel={() => setColorSchemeModalVisible(false)}
        onSchemeUpdated={fetchActiveColorSchemes}
      />

      {/* 미리보기 모달 */}
      <RestaurantPreviewModal
        visible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        restaurant={previewModalVisible ? getPreviewRestaurantData() : null}
        displayImages={previewModalVisible ? getPreviewDisplayImages() : []}
      />
    </Space>
  );
};

export default RestaurantAdd;
